import React, { useState, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

interface DonutWithLegendProps {
  title: string;
  dataAll: { name: string; value: number }[];
  dataYear: { name: string; value: number }[];
  dataTrim: { name: string; value: number }[];
}

/** A cohesive palette */
const COLORS = ["#FA7356", "#F3B93A", "#7655FA", "#5699FA", "#7EDDD5"];

const DonutWithLegend: React.FC<DonutWithLegendProps> = ({
  title,
  dataAll,
  dataYear,
  dataTrim,
}) => {
  const [range, setRange] = useState("Depuis toujours");
  const [expanded, setExpanded] = useState(false);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRange(e.target.value);
    setExpanded(false);
  };

  const displayedData = useMemo(() => {
    switch (range) {
      case "Cette année":
        return dataYear;
      case "Ce trimestre":
        return dataTrim;
      default:
        return dataAll;
    }
  }, [range, dataAll, dataYear, dataTrim]);

  // Legend shows only first 6 items when collapsed
  const legendData = expanded ? displayedData : displayedData.slice(0, 6);
  const totalValue = displayedData.reduce((sum, d) => sum + d.value, 0);

  const CustomTooltipPie = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-neutral-700 p-2 border rounded shadow text-sm text-neutral-400">
          <p>{label}</p>
          <p className="flex items-center gap-2">
            <div className="h-2 w-2 bg-[#95DE64] rounded-full"></div>
            Revenus :{" "}
            <span className="text-white">
              {payload![0].value.toLocaleString("fr-FR")} €
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
        <h3 className="font-semibold text-xl">{title}</h3>
        <select
          className="border border-gray-300 rounded p-1 text-sm mt-2 sm:mt-0"
          value={range}
          onChange={handleSelectChange}
        >
          <option>Depuis toujours</option>
          <option>Cette année</option>
          <option>Ce trimestre</option>
        </select>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Donut Chart */}
        <div className="w-full md:w-1/3" style={{ height: 250 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={displayedData}
                dataKey="value"
                nameKey="name"
                startAngle={90}
                endAngle={-270}
                innerRadius={70}
                outerRadius={100}
              >
                {displayedData.map((entry, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={COLORS[idx % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltipPie />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="w-full md:w-2/3">
          <div className="flex justify-between items-center mb-2 font-semibold text-gray-700">
            <span>Total</span>
            <span>{totalValue.toLocaleString("fr-FR")} €</span>
          </div>
          <div className="flex flex-col gap-2 overflow-auto">
            {legendData.map((item, idx) => {
              const ratio = (item.value / totalValue) * 100 || 0;
              return (
                <div key={idx}>
                  <div className="flex justify-between mb-1 text-sm text-gray-600">
                    <span>{item.name}</span>
                    <span>{item.value.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded">
                    <div
                      className="h-2 rounded"
                      style={{
                        width: `${ratio}%`,
                        backgroundColor: COLORS[idx % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {displayedData.length > 6 && (
            <button
              className="mt-4 text-blue-500 text-sm hover:underline"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Voir moins" : "Voir plus"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonutWithLegend;
